'use client';
import { useEffect, RefObject } from 'react';

/**
 * Renders mermaid diagrams inside a container element.
 * Finds all `div[data-type="mermaidDiagram"]` or `span[data-type="mermaidDiagram"]`
 * elements and replaces them with rendered SVG diagrams.
 */
export function useMermaidRenderer(
  containerRef: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const attemptRender = () => {
      const mermaidElements = container.querySelectorAll<HTMLElement>(
        'div[data-type="mermaidDiagram"], span[data-type="mermaidDiagram"]',
      );
      if (mermaidElements.length === 0) return;

      const render = async () => {
        try {
          // Indirect the module name so webpack can't statically resolve it
          // (mermaid is a heavy optional dep loaded only when diagrams are on screen)
          const mod = 'mermaid';
          // @ts-ignore – module resolved at runtime, not at build time
          const mermaid = (await import(/* webpackIgnore: true */ mod)).default;
          // TODO: If feed pages ever have many posts with mermaid diagrams,
          // hoist mermaid.initialize() to a module-level singleton to avoid
          // re-initializing per TextWithLinks instance.
          mermaid.initialize({
            startOnLoad: false,
            theme: 'neutral',
            securityLevel: 'loose',
            fontFamily: 'inherit',
          });

          let counter = 0;
          for (const el of Array.from(mermaidElements)) {
            if (cancelled) return;
            if (el.hasAttribute('data-rendered')) continue;
            // Try data-code first, fall back to innerHTML (DOMPurify may strip data-code)
            let code = el.getAttribute('data-code');
            if (!code) {
              // Use a temp element to decode HTML entities (&gt; -> >) and convert <br> to newlines
              const temp = document.createElement('div');
              temp.innerHTML = el.innerHTML.replace(/<br\s*\/?>/gi, '\n');
              code = (temp.textContent || temp.innerText || '').trim();
            }
            if (!code) continue;

            try {
              counter++;
              const id = `mermaid-ro-${Date.now()}-${counter}`;
              const { svg } = await mermaid.render(id, code);
              el.setAttribute('data-rendered', 'true');
              el.innerHTML = svg;
              el.style.cssText =
                'display: block; border: 1px solid hsl(var(--border)); border-radius: 8px; padding: 16px; margin: 8px 0; overflow: auto;';
              const svgEl = el.querySelector<SVGSVGElement>('svg');
              if (svgEl) {
                svgEl.style.width = '100%';
                svgEl.style.maxWidth = '100%';
                svgEl.style.height = 'auto';
              }
            } catch (err: unknown) {
              console.error('[useMermaidRenderer] render error:', err);
              el.setAttribute('data-rendered', 'true');
              el.innerHTML = `<pre style="color: red; font-size: 13px; padding: 12px;">${
                err instanceof Error ? err.message : 'Invalid mermaid syntax'
              }</pre>`;
            }
          }
        } catch (err: unknown) {
          console.error('[useMermaidRenderer] Failed to load mermaid library:', err);
        }
      };

      render();
    };

    // Delay to let React hydration settle before touching the DOM
    timeoutId = setTimeout(() => {
      if (!cancelled) attemptRender();
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
